import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, TrendingUp, CheckCircle2, Clock, 
  AlertTriangle, Sparkles, RefreshCw, Loader2,
  Trophy, Medal, Crown, Target, Zap, User, Users,
  Download, FileSpreadsheet, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useReportStore, useUIStore, useTeamStore, useAdminStore, useAuthStore } from '../lib/store';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';
import { 
  PieChart, Pie, Cell, ResponsiveContainer, 
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend 
} from 'recharts';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReportsPage = () => {
  const { summary, insights, topPerformers, fetchSummary, fetchInsights, fetchTopPerformers } = useReportStore();
  const { teams, fetchTeams } = useTeamStore();
  const { users, fetchUsers } = useAdminStore();
  const { user: currentUser } = useAuthStore();
  const { language } = useUIStore();
  const { t } = useTranslation(language);
  
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [userReport, setUserReport] = useState(null);
  const [isLoadingUserReport, setIsLoadingUserReport] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedUserId && selectedUserId !== 'all') {
      fetchUserReport(selectedUserId);
    } else {
      setUserReport(null);
    }
  }, [selectedUserId]);

  const loadData = async () => {
    try {
      const promises = [fetchSummary(), fetchInsights(), fetchTopPerformers(), fetchTeams()];
      if (currentUser?.is_admin) {
        promises.push(fetchUsers());
      }
      await Promise.all(promises);
    } catch (error) {
      toast.error(error.message || t('error'));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const fetchUserReport = async (userId) => {
    setIsLoadingUserReport(true);
    try {
      const token = useAuthStore.getState().token;
      const res = await fetch(`${API_URL}/api/users/${userId}/report`, {
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        }
      });
      if (res.ok) {
        const data = await res.json();
        setUserReport(data);
      } else {
        toast.error('Failed to load user report');
      }
    } catch (error) {
      console.error('Failed to fetch user report:', error);
      toast.error('Failed to load user report');
    } finally {
      setIsLoadingUserReport(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadData();
    if (selectedUserId !== 'all') {
      await fetchUserReport(selectedUserId);
    }
    toast.success(language === 'hi' ? 'रिपोर्ट अपडेट हुई' : 'Reports updated');
  };

  // Export to PDF
  const exportToPDF = () => {
    try {
      const doc = new jsPDF();
      const reportData = selectedUserId !== 'all' && userReport ? userReport : { summary };
      const userName = (selectedUserId !== 'all' && userReport?.user?.name) ? userReport.user.name : 'Overall';
      
      // Title
      doc.setFontSize(20);
      doc.setTextColor(0, 212, 255);
      doc.text('OSLO One - Report', 14, 22);
      
      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text(`Report for: ${userName}`, 14, 32);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 40);
      
      // Summary table
      doc.setFontSize(14);
      doc.setTextColor(0);
      doc.text('Summary', 14, 55);
      
      const summaryData = reportData.summary || summary;
      autoTable(doc, {
        startY: 60,
        head: [['Metric', 'Value']],
        body: [
          ['Total Projects', summaryData?.projects || 0],
          ['Total Tasks', summaryData?.tasks?.total || ((summaryData?.tasks?.todo || 0) + (summaryData?.tasks?.in_progress || 0) + (summaryData?.tasks?.done || 0)) || 0],
          ['To Do', summaryData?.tasks?.todo || 0],
          ['In Progress', summaryData?.tasks?.in_progress || 0],
          ['Completed', summaryData?.tasks?.done || 0],
        ],
        theme: 'striped',
        headStyles: { fillColor: [0, 153, 255] }
      });
      
      // Productivity if user report
      if (selectedUserId !== 'all' && userReport) {
        const finalY = doc.lastAutoTable?.finalY || 120;
        doc.text('Productivity', 14, finalY + 15);
        autoTable(doc, {
          startY: finalY + 20,
          head: [['Metric', 'Value']],
          body: [
            ['Productivity Score', `${userReport.productivity_score || 0}%`],
            ['Completion Rate', `${userReport.completion_rate || 0}%`],
            ['Completed This Week', userReport.completed_this_week || 0],
          ],
          theme: 'striped',
          headStyles: { fillColor: [0, 153, 255] }
        });
      }
      
      // Top Performers
      if (topPerformers && topPerformers.length > 0 && selectedUserId === 'all') {
        const finalY = doc.lastAutoTable?.finalY || 120;
        doc.text('Top Performers', 14, finalY + 15);
        autoTable(doc, {
          startY: finalY + 20,
          head: [['Rank', 'Name', 'Productivity Score', 'Tasks Completed']],
          body: topPerformers.map((p, i) => [
            i + 1,
            p.user?.name || 'Unknown',
            `${p.productivity_score || 0}%`,
            p.metrics?.completed || 0
          ]),
          theme: 'striped',
          headStyles: { fillColor: [0, 153, 255] }
        });
      }
      
      doc.save(`oslo_report_${userName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    try {
      const reportData = selectedUserId !== 'all' && userReport ? userReport : { summary };
      const userName = (selectedUserId !== 'all' && userReport?.user?.name) ? userReport.user.name : 'Overall';
      const summaryData = reportData.summary || summary;
      
      // Summary sheet
      const summarySheet = [
        ['OSLO One Report'],
        [`Report for: ${userName}`],
        [`Generated: ${new Date().toLocaleDateString()}`],
        [],
        ['Metric', 'Value'],
        ['Total Projects', summaryData?.projects || 0],
        ['Total Tasks', summaryData?.tasks?.total || ((summaryData?.tasks?.todo || 0) + (summaryData?.tasks?.in_progress || 0) + (summaryData?.tasks?.done || 0)) || 0],
        ['To Do', summaryData?.tasks?.todo || 0],
        ['In Progress', summaryData?.tasks?.in_progress || 0],
        ['Completed', summaryData?.tasks?.done || 0],
      ];
      
      if (selectedUserId !== 'all' && userReport) {
        summarySheet.push(
          [],
          ['Productivity Metrics'],
          ['Productivity Score', `${userReport.productivity_score || 0}%`],
          ['Completion Rate', `${userReport.completion_rate || 0}%`],
          ['Completed This Week', userReport.completed_this_week || 0],
        );
      }
      
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(summarySheet);
      XLSX.utils.book_append_sheet(wb, ws, 'Summary');
      
      // Top Performers sheet
      if (topPerformers && topPerformers.length > 0 && selectedUserId === 'all') {
        const performersData = [
          ['Rank', 'Name', 'Email', 'Productivity Score', 'Tasks Completed', 'Completion Rate'],
          ...topPerformers.map((p, i) => [
            i + 1,
            p.user?.name || 'Unknown',
            p.user?.email || '',
            `${p.productivity_score || 0}%`,
            p.metrics?.completed || 0,
            `${p.metrics?.completion_rate || 0}%`
          ])
        ];
        const ws2 = XLSX.utils.aoa_to_sheet(performersData);
        XLSX.utils.book_append_sheet(wb, ws2, 'Top Performers');
      }
      
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(data, `oslo_report_${userName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Excel downloaded');
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error('Failed to export Excel');
    }
  };

  // Use user-specific data if selected, otherwise overall
  const displaySummary = (selectedUserId !== 'all' && userReport?.summary) ? userReport.summary : summary;
  
  const pieData = displaySummary ? [
    { name: t('todo'), value: displaySummary.tasks?.todo || 0, color: 'hsl(var(--muted-foreground))' },
    { name: t('in_progress'), value: displaySummary.tasks?.in_progress || 0, color: 'hsl(var(--chart-4))' },
    { name: t('done'), value: displaySummary.tasks?.done || 0, color: 'hsl(var(--chart-3))' },
  ] : [];

  const barData = displaySummary ? [
    { name: t('todo'), count: displaySummary.tasks?.todo || 0 },
    { name: t('in_progress'), count: displaySummary.tasks?.in_progress || 0 },
    { name: t('done'), count: displaySummary.tasks?.done || 0 },
  ] : [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-primary" />
            {t('reports')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {selectedUserId !== 'all' && userReport?.user 
              ? `${language === 'hi' ? 'रिपोर्ट:' : 'Report for:'} ${userReport.user.name}`
              : (language === 'hi' ? 'अपने प्रोजेक्ट और टास्क की प्रगति देखें' : 'View your project and task progress')
            }
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* User Filter (Admin Only) */}
          {currentUser?.is_admin && (
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger className="w-48">
                <User className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Select user" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    {language === 'hi' ? 'सभी (कुल)' : 'All (Overall)'}
                  </span>
                </SelectItem>
                {users.map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {/* Export Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Download className="h-4 w-4 mr-2" />
                {language === 'hi' ? 'डाउनलोड' : 'Export'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportToPDF}>
                <FileText className="h-4 w-4 mr-2 text-red-500" />
                {language === 'hi' ? 'PDF डाउनलोड करें' : 'Download PDF'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportToExcel}>
                <FileSpreadsheet className="h-4 w-4 mr-2 text-green-500" />
                {language === 'hi' ? 'Excel डाउनलोड करें' : 'Download Excel'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button
            variant="secondary"
            onClick={handleRefresh}
            disabled={isRefreshing}
            data-testid="refresh-reports-btn"
          >
            {isRefreshing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            {language === 'hi' ? 'रिफ्रेश' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* User-specific stats header */}
      {selectedUserId !== 'all' && userReport?.user && (
        <Card className="glass border-primary/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Avatar className="h-12 w-12">
                <AvatarFallback className="bg-primary/20 text-primary font-bold">
                  {userReport.user.name?.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <h2 className="font-heading text-lg font-semibold">{userReport.user.name}</h2>
                <p className="text-sm text-muted-foreground">{userReport.user.email}</p>
              </div>
              <div className="ml-auto flex items-center gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary">{userReport.summary?.tasks?.total || 0}</p>
                  <p className="text-xs text-muted-foreground">{language === 'hi' ? 'कुल टास्क' : 'Total Tasks'}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-chart-3">{userReport.summary?.tasks?.done || 0}</p>
                  <p className="text-xs text-muted-foreground">{language === 'hi' ? 'पूर्ण' : 'Completed'}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-chart-4">{userReport.productivity_score || 0}%</p>
                  <p className="text-xs text-muted-foreground">{language === 'hi' ? 'प्रोडक्टिविटी' : 'Productivity'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="glass card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">{t('total_projects')}</p>
                  <p className="font-heading text-3xl font-bold mt-1">{displaySummary?.projects || 0}</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center">
                  <BarChart3 className="h-6 w-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="glass card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">{t('total_tasks')}</p>
                  <p className="font-heading text-3xl font-bold mt-1">{displaySummary?.tasks?.total || (displaySummary?.tasks?.todo + displaySummary?.tasks?.in_progress + displaySummary?.tasks?.done) || 0}</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-accent/20 flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-accent" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="glass card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">{t('completed_this_week')}</p>
                  <p className="font-heading text-3xl font-bold mt-1">{(selectedUserId !== 'all' && userReport) ? (userReport.completed_this_week || 0) : (summary?.completed_this_week || 0)}</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-chart-3/20 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-chart-3" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="glass card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">{t('productivity_score')}</p>
                  <p className="font-heading text-3xl font-bold mt-1">{(selectedUserId !== 'all' && userReport) ? (userReport.productivity_score || 0) : (summary?.productivity_score || 0)}%</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-chart-4/20 flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-chart-4" />
                </div>
              </div>
              <Progress value={(selectedUserId !== 'all' && userReport) ? (userReport.productivity_score || 0) : (summary?.productivity_score || 0)} className="mt-3 h-2" />
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Top Performers Leaderboard */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
      >
        <Card className="glass">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-400" />
              {language === 'hi' ? 'टॉप परफॉर्मर्स' : 'Top Performers'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topPerformers.length === 0 ? (
              <div className="text-center py-8">
                <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading text-lg font-semibold mb-2">
                  {language === 'hi' ? 'अभी तक कोई डेटा नहीं' : 'No Data Yet'}
                </h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  {language === 'hi' 
                    ? 'टीम के सदस्यों को टास्क असाइन करें ताकि प्रोडक्टिविटी ट्रैक हो।'
                    : 'Assign tasks to team members to track productivity.'}
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-4">
                {topPerformers.map((performer, index) => {
                  const getRankIcon = (rank) => {
                    if (rank === 1) return <Crown className="h-6 w-6 text-yellow-400" />;
                    if (rank === 2) return <Medal className="h-6 w-6 text-gray-300" />;
                    return <Medal className="h-6 w-6 text-amber-600" />;
                  };

                  const getRankBg = (rank) => {
                    if (rank === 1) return 'bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border-yellow-500/40';
                    if (rank === 2) return 'bg-gradient-to-br from-gray-300/20 to-gray-400/20 border-gray-400/40';
                    return 'bg-gradient-to-br from-amber-600/20 to-amber-700/20 border-amber-600/40';
                  };

                  return (
                    <motion.div
                      key={performer.user.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.1 * index }}
                      className={`relative p-5 rounded-xl border ${getRankBg(performer.rank)}`}
                    >
                      {/* Rank Badge */}
                      <div className="absolute -top-3 -right-3">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                          performer.rank === 1 ? 'bg-yellow-500' : performer.rank === 2 ? 'bg-gray-400' : 'bg-amber-600'
                        }`}>
                          <span className="text-lg font-bold text-white">#{performer.rank}</span>
                        </div>
                      </div>

                      {/* User Info */}
                      <div className="flex items-center gap-3 mb-4">
                        <Avatar className="h-12 w-12 border-2 border-white/10">
                          <AvatarFallback className="bg-primary/20 text-primary font-bold">
                            {performer.user.name?.substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">{performer.user.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{performer.user.email}</p>
                        </div>
                        {getRankIcon(performer.rank)}
                      </div>

                      {/* Productivity Score */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-muted-foreground">
                            {language === 'hi' ? 'प्रोडक्टिविटी स्कोर' : 'Productivity Score'}
                          </span>
                          <span className="text-lg font-bold text-primary">{performer.productivity_score}%</span>
                        </div>
                        <Progress value={performer.productivity_score} className="h-2" />
                      </div>

                      {/* Metrics */}
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-chart-3" />
                          <span>{performer.metrics.completed} {language === 'hi' ? 'पूर्ण' : 'done'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-chart-4" />
                          <span>{performer.metrics.in_progress} {language === 'hi' ? 'प्रगति' : 'active'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Target className="h-4 w-4 text-accent" />
                          <span>{performer.metrics.completion_rate}% {language === 'hi' ? 'दर' : 'rate'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Zap className="h-4 w-4 text-primary" />
                          <span>{performer.metrics.completed_this_week} {language === 'hi' ? 'इस हफ्ते' : 'weekly'}</span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Pie Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="glass">
            <CardHeader>
              <CardTitle className="font-heading">
                {language === 'hi' ? 'टास्क वितरण' : 'Task Distribution'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(displaySummary?.tasks?.total || (displaySummary?.tasks?.todo + displaySummary?.tasks?.in_progress + displaySummary?.tasks?.done)) > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center">
                  <p className="text-muted-foreground">
                    {language === 'hi' ? 'कोई डेटा नहीं' : 'No data available'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Bar Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Card className="glass">
            <CardHeader>
              <CardTitle className="font-heading">
                {language === 'hi' ? 'टास्क स्थिति' : 'Task Status'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(displaySummary?.tasks?.total || (displaySummary?.tasks?.todo + displaySummary?.tasks?.in_progress + displaySummary?.tasks?.done)) > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barData}>
                      <XAxis 
                        dataKey="name" 
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                      />
                      <YAxis 
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                      />
                      <Tooltip
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Bar 
                        dataKey="count" 
                        fill="hsl(var(--primary))" 
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center">
                  <p className="text-muted-foreground">
                    {language === 'hi' ? 'कोई डेटा नहीं' : 'No data available'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* AI Insights */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <Card className="glass">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              {t('ai_insights')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {insights.length === 0 ? (
              <div className="text-center py-12">
                <Sparkles className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading text-lg font-semibold mb-2">
                  {language === 'hi' ? 'अभी तक कोई इनसाइट नहीं' : 'No Insights Yet'}
                </h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  {language === 'hi' 
                    ? 'प्रोजेक्ट और टास्क बनाएं ताकि AI आपके लिए इनसाइट जनरेट कर सके।'
                    : 'Create projects and tasks to generate AI-powered insights.'}
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {insights.map((insight, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index }}
                    className={`p-4 rounded-xl border ${
                      insight.type === 'risk' 
                        ? 'border-destructive/30 bg-destructive/5' 
                        : insight.type === 'bottleneck'
                        ? 'border-chart-4/30 bg-chart-4/5'
                        : 'border-primary/30 bg-primary/5'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        insight.type === 'risk' 
                          ? 'bg-destructive/20' 
                          : insight.type === 'bottleneck'
                          ? 'bg-chart-4/20'
                          : 'bg-primary/20'
                      }`}>
                        {insight.type === 'risk' ? (
                          <AlertTriangle className="h-4 w-4 text-destructive" />
                        ) : insight.type === 'bottleneck' ? (
                          <Clock className="h-4 w-4 text-chart-4" />
                        ) : (
                          <Sparkles className="h-4 w-4 text-primary" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{insight.title}</p>
                        <p className="text-xs text-muted-foreground mt-1">{insight.description}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default ReportsPage;
